import { Component, OnDestroy, Inject } from '@angular/core';
import { Subject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { takeUntil } from 'rxjs/operators';
import { MessageService } from 'src/app/global/services/message/message.abstract.service';
import { UserRecord, Link } from '../../user.model';
import { UserService } from '../../service/user.abstract.service';

/**
 * Lets an admin accept a pending family invitation on behalf of the citizen who has not answered it yet.
 * Meant for one-off citizen support: the backend sets both familyApproved and cityApproved on the two links.
 */
@Component({
    selector: 'app-force-accept-family-link-dialog',
    templateUrl: './force-accept-family-link-dialog.component.html',
    styleUrls: ['./force-accept-family-link-dialog.component.scss'],
})
export class ForceAcceptFamilyLinkDialogComponent implements OnDestroy {
    destroy$ = new Subject();

    processingSubmit = false;

    constructor(
        private translateService: TranslateService,
        private dialog: MatDialogRef<ForceAcceptFamilyLinkDialogComponent>,
        private userService: UserService,
        @Inject(MAT_DIALOG_DATA) public data: { userRecord: UserRecord; link: Link },
        private messageService: MessageService,
    ) {}

    get linkLabel(): string {
        return this.translateService.instant(`pages.userFamily.link.${this.data?.link?.link.toLowerCase()}`);
    }

    closeDialog(): void {
        this.dialog.close();
    }

    /**
     * Blocks every close path (buttons, Escape, backdrop) while the request is in flight,
     * so the dialog can't be torn down between the server accepting the link and the list refresh.
     */
    setProcessing(processing: boolean): void {
        this.processingSubmit = processing;
        this.dialog.disableClose = processing;
    }

    submit(): void {
        this.setProcessing(true);

        this.userService
            .patchApproveRefuseFamilyLink({
                id: this.data.userRecord.id,
                family_member_id: this.data.link.userId._id,
                confirmed: true,
            })
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                () => {
                    this.messageService.success(this.translateService.instant('pages.userFamily.forceAccept.messages.success'));
                    this.setProcessing(false);
                    this.dialog.close(true);
                },
                (error) => {
                    this.messageService.error(error?.error?.messages?.[0], this.translateService.instant('pages.userFamily.forceAccept.messages.error'));
                    this.setProcessing(false);
                },
            );
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
